// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useSetProviderAppRewardBeneficiaries } from '@hooks/mutations/registry/configuration/useSetProviderAppRewardBeneficiaries';
import { Beneficiary, useBeneficiaryList } from '@hooks/other/useBeneficiaryList';
import React from 'react';
import { Add, Delete, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { InstrumentConfiguration } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Configuration/Instrument';
import { TextInputLabel } from '../../../components/Form/TextInput';
import TemplateContract from '../../../utils/TemplateContract';
import { isPositiveFloat } from '../../../utils/isPositiveFloat';
import { isTotalWeightValid } from '../../../utils/isTotalWeightValid';

interface ModifyProvierAppRewardBeneficiariesDialogProps {
  open: boolean;
  onClose: () => void;
  disableDialog?: boolean;
  intrumentConfiguration?: TemplateContract<InstrumentConfiguration>;
}
export const ModifyProvierAppRewardBeneficiariesDialog: React.FC<
  ModifyProvierAppRewardBeneficiariesDialogProps
> = ({ open, onClose, intrumentConfiguration, disableDialog }) => {
  const providerAppRewardBeneficiaries =
    intrumentConfiguration?.payload.providerAppRewardBeneficiaries;
  const {
    hasWeightError,
    beneficiaries,
    isValid,
    addBeneficiary,
    updateBeneficiary,
    removeBeneficiary,
  } = useBeneficiaryList(providerAppRewardBeneficiaries || []);
  const hasBeneficiares = !!beneficiaries.length;
  const handleInputChange = (id: string, key: keyof Omit<Beneficiary, 'id'>, value: string) => {
    updateBeneficiary(id, key, value);
  };
  const isSumWeightValid = isTotalWeightValid(beneficiaries.map((b) => b.weight));
  const { submit, status } = useSetProviderAppRewardBeneficiaries();

  const onModify = async () => {
    if (!intrumentConfiguration) {
      throw new Error('No instrument configuration');
    }

    await submit({
      cid: intrumentConfiguration.contractId,
      argument: {
        providerAppRewardBeneficiaries: hasBeneficiares ? beneficiaries : null,
      },
    });
    onClose();
  };
  const isSubmitDisabled = !isValid || hasWeightError || !isSumWeightValid;
  const form = (
    <>
      <Box sx={{ mb: 1, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <InfoOutlined sx={{ mr: 1 }} />
        <Typography variant="caption">The sum of weights must be equal to 1.</Typography>
      </Box>
      {beneficiaries.map((b, i) => (
        <Box
          key={b.id}
          sx={{
            mb: beneficiaries.length > 0 ? 1 : 0,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            <TextInputLabel label="Beneficiary" />
            <TextField
              sx={{ mb: 1 }}
              size="small"
              type="text"
              fullWidth
              placeholder="Beneficiary"
              value={b.beneficiary}
              onChange={(e) => handleInputChange(b.id, 'beneficiary', e.target.value)}
            />
            <TextInputLabel label="Weight" />
            <TextField
              size="small"
              type="text"
              fullWidth
              placeholder="Weight"
              value={b.weight}
              onChange={(e) => {
                if (isPositiveFloat(e.target.value)) {
                  handleInputChange(b.id, 'weight', e.target.value);
                }
              }}
            />
            {beneficiaries.length > 1 && i !== beneficiaries.length - 1 && (
              <Divider sx={{ mt: 3, mb: 1 }} />
            )}
          </Box>
          <IconButton sx={{ ml: 1, mt: 1 }} color="error" onClick={() => removeBeneficiary(b.id)}>
            <Delete color="error" />
          </IconButton>
        </Box>
      ))}
      <Button
        startIcon={<Add />}
        sx={{ mt: 1 }}
        fullWidth={!hasBeneficiares}
        variant="text"
        onClick={addBeneficiary}
      >
        Add Beneficiary
      </Button>
      <Divider sx={{ mt: 2, mb: 2 }} />
      {hasWeightError && (
        <Typography sx={{ mb: 1 }} variant="caption" color="error">
          Individual weight must be between 0 and 1
        </Typography>
      )}
      {!isSumWeightValid && (
        <Typography sx={{ mb: 1 }} variant="caption" color="error">
          Total sum of weights must be equal to 1.
        </Typography>
      )}
      <Button
        disabled={isSubmitDisabled}
        onClick={() => {
          onModify()
            .then(() => {})
            .catch(() => {});
        }}
      >
        {status === 'pending' ? <CircularProgress color="inherit" size={20} /> : 'Save'}
      </Button>
      <Button sx={{ mt: 1 }} variant="outlined" onClick={onClose}>
        Cancel
      </Button>
    </>
  );
  if (disableDialog) {
    return <Box sx={{ p: 2, width: '100%', display: 'flex', flexDirection: 'column' }}>{form}</Box>;
  }
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent sx={{ width: 400, display: 'flex', flexDirection: 'column' }}>
        {form}
      </DialogContent>
    </Dialog>
  );
};
